import { normalizeTemplateFacets } from './templateTaxonomy';
import { inferTemplateSlots } from './templateSlotInference';
/**
 * Elegir template sin gastar IA.
 *
 * La IA decide QUÉ se quiere —una sección de precios, para una clínica, con
 * estilo elegante— y este módulo decide CON CUÁL: puntuando las facetas que el
 * admin puso al publicar. Lo usan el generador de la primera página y el chat
 * del editor, para que los dos elijan con el mismo criterio, y ninguno de los
 * dos le manda el catálogo entero al modelo.
 */
/** Minúsculas y sin acentos: «Sección» y «seccion» son la misma palabra. */
export function normalizeSearchText(value) {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
/**
 * Cómo se nombra cada categoría al pedirla, en inglés y en español. Frases ya
 * normalizadas. El orden importa poco: gana la que aparece antes en el mensaje.
 */
export const CATEGORY_TERMS = {
    navbar: ['navbar', 'nav bar', 'navigation', 'navegacion', 'menu', 'header', 'cabecera', 'barra de navegacion'],
    hero: ['hero', 'portada', 'above the fold', 'encabezado principal', 'intro'],
    features: ['features', 'feature', 'benefits', 'beneficios', 'caracteristicas', 'services', 'servicios', 'ventajas'],
    cards: ['cards', 'tarjetas'],
    bento: ['bento'],
    gallery: ['gallery', 'galeria', 'portfolio', 'portafolio', 'photos', 'fotos', 'imagenes'],
    logos: ['logos', 'logo cloud', 'clients', 'clientes', 'partners', 'socios', 'marcas', 'brands'],
    stats: ['stats', 'statistics', 'estadisticas', 'numbers', 'numeros', 'metrics', 'metricas', 'cifras', 'kpi'],
    testimonials: ['testimonials', 'testimonial', 'testimonios', 'reviews', 'resenas', 'opiniones', 'social proof'],
    pricing: ['pricing', 'prices', 'precios', 'plans', 'planes', 'tarifas'],
    faq: ['faq', 'faqs', 'questions', 'preguntas', 'dudas'],
    cta: ['cta', 'call to action', 'llamado a la accion', 'llamada a la accion'],
    contact: ['contact', 'contacto', 'contactanos', 'get in touch', 'form', 'formulario'],
    menu: ['menu', 'menú', 'carta', 'platillos', 'dishes', 'food menu', 'nuestra carta'],
    reservations: ['opentable', 'open table', 'book a table', 'table booking', 'reservar mesa', 'reserva de mesa', 'reservaciones de mesa'],
    map: ['map', 'mapa', 'location', 'locations', 'ubicacion', 'ubicaciones', 'sucursal', 'sucursales', 'branches', 'find us', 'como llegar', 'directions', 'donde estamos'],
    team: ['team', 'equipo', 'staff', 'our people'],
    steps: ['steps', 'pasos', 'process', 'proceso', 'how it works', 'como funciona', 'timeline'],
    content: ['content', 'contenido', 'article', 'articulo', 'about us', 'about', 'sobre nosotros', 'acerca de', 'nosotros'],
    comparison: ['comparison', 'comparacion', 'comparativa', 'compare', 'comparar'],
    banner: ['announcement', 'anuncio', 'promo', 'promocion', 'aviso', 'banner'],
    blog: ['blog', 'posts', 'articles', 'articulos', 'news', 'noticias'],
    commerce: ['products', 'productos', 'shop', 'tienda', 'store', 'catalog', 'catalogo'],
    footer: ['footer', 'pie de pagina'],
};
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Las categorías que nombra un mensaje, en el orden en que aparecen. */
export function detectTemplateCategories(message) {
    const text = normalizeSearchText(message);
    const found = [];
    for (const [category, terms] of Object.entries(CATEGORY_TERMS)) {
        let at = -1;
        for (const term of terms) {
            const match = new RegExp(`\\b${escapeRegExp(term)}\\b`).exec(text);
            if (match && (at === -1 || match.index < at))
                at = match.index;
        }
        if (at >= 0)
            found.push({ category, at });
    }
    return found.sort((left, right) => left.at - right.at).map((entry) => entry.category);
}
// Los verbos en español como raíz (agrega, agregar, agrégale…); los ingleses
// como palabra entera, para que «address» no cuente como «add».
const ADD_VERB = /\b(?:(?:agreg|anad|insert|crea|pon|quier|necesit)\w*|(?:add|insert|create|put|include|want|need)(?:s|ed|ing)?\b|give me\b)/;
const SECTION_WORD = /\b(seccion(?:es)?|bloque(?:s)?|apartado(?:s)?|sections?|blocks?)\b/;
// «pon más espaciado entre los bloques» nombra los bloques como SITIO del
// cambio, no como algo que crear.
const STYLES_EXISTING = /\b(espaci|separacion|aire|gap|margen|padding|hueco|junto|pegad|distancia|spacing|space between|margin|closer|apart)\w*/;
// Cualquier otra cosa en la misma frase va por la ruta general.
const ALSO_WANTS_MORE = /\b(color|colou?r|paleta|palette|tono|letra|fuente|tipograf|font|typography|traduc|translat|reescrib|rewrite|borra|elimina|delete|remove|duplica|duplicate|mueve|move)\w*/;
/**
 * «Agrega una sección de precios» / «add a pricing section»: insertar un
 * bloque publicado. En los dos idiomas: antes sólo se reconocía el español y
 * en inglés la petición llegaba al modelo sin bloques, que contestaba que no
 * había ninguno.
 */
export function messageRequestsSectionInsert(message) {
    const text = normalizeSearchText(message);
    return ADD_VERB.test(text) && SECTION_WORD.test(text) && !STYLES_EXISTING.test(text) && !ALSO_WANTS_MORE.test(text);
}
/** Más amplio: el mensaje PUEDE acabar insertando, así que conviene tener bloques a mano. */
export function messageMayInsertSection(message) {
    const text = normalizeSearchText(message);
    return ADD_VERB.test(text) && (SECTION_WORD.test(text) || detectTemplateCategories(text).length > 0);
}
function walk(nodes, visit) {
    for (const node of nodes) {
        visit(node);
        if (node.children?.length)
            walk(node.children, visit);
    }
}
function slotOf(node) {
    return typeof node.aiSlot === 'string' ? node.aiSlot.trim() : '';
}
/**
 * `media` y `columns` salen de lo que hay dentro: si lleva imágenes, iconos o
 * vídeo, y cuántas columnas tiene su primera cuadrícula. El admin no tiene que
 * marcarlos, y no pueden quedarse desfasados al editar el template.
 */
export function deriveTemplateFacets(elements) {
    const media = new Set();
    let columns;
    walk(elements, (node) => {
        const slot = slotOf(node);
        if (node.type === 'image') {
            if (slot.startsWith('media.avatar'))
                media.add('avatar');
            else if (slot === 'media.logo')
                media.add('logo');
            else
                media.add('image');
        }
        if (node.type === 'avatarGroup')
            media.add('avatar');
        if (node.type === 'icon' || slot.startsWith('icon'))
            media.add('icon');
        if (node.type === 'video')
            media.add('video');
        if (!columns) {
            const desktop = node.styles?.desktop;
            const grid = typeof desktop?.gridTemplateColumns === 'string' ? desktop.gridTemplateColumns : '';
            const repeat = /repeat\(\s*(\d+)/.exec(grid);
            const count = repeat ? Number(repeat[1]) : grid ? grid.trim().split(/\s+/).length : 0;
            if (count >= 1 && count <= 6)
                columns = String(count);
        }
    });
    return {
        media: media.size ? [...media] : ['none'],
        ...(columns ? { columns } : {}),
    };
}
const TEXT_NODE_TYPES = new Set(['text', 'richHeading', 'button', 'link', 'badge']);
/**
 * Qué le falta a un template para que la IA lo elija bien y lo rellene entero.
 * Lo usan la ficha del admin, para enseñarlo, y el catálogo de la IA, que deja
 * fuera lo que no está listo en vez de insertar algo que no sabe rellenar.
 */
export function templateAiReadiness(input) {
    const facets = normalizeTemplateFacets(input.facets);
    const blocking = [];
    const advice = [];
    if (!facets.category)
        blocking.push('category');
    if (facets.manualOnly)
        blocking.push('manualOnly');
    // La descripción ayuda a desempatar y a leer el catálogo, pero la elección va
    // por facetas: sin ella el template se sigue usando.
    if (!input.description || input.description.trim().length < 12)
        advice.push('description');
    let slotted = 0;
    let unslottedTexts = 0;
    walk(input.elements, (node) => {
        if (slotOf(node))
            slotted += 1;
        else if (TEXT_NODE_TYPES.has(node.type) && typeof node.content === 'string' && node.content.trim())
            unslottedTexts += 1;
    });
    // Sin marcas, se deducen (`inferTemplateSlots`): la IA lo usa igual, pero
    // marcarlos a mano sale más preciso. Sólo sin nada que deducir queda fuera.
    if (slotted === 0) {
        if (inferTemplateSlots(input.elements, facets.category).inferred > 0)
            advice.push('slots');
        else
            blocking.push('slots');
    }
    // Estilo, giro y objetivo no se piden: un bloque es estructura, y cómo se ve
    // o para quién es lo decide el sitio (su receta de estilo y sus textos).
    if (!facets.layout?.length)
        advice.push('layout');
    if (!facets.colorScheme?.length)
        advice.push('colorScheme');
    if (slotted > 0 && unslottedTexts > 0)
        advice.push('unslottedText');
    return { ready: blocking.length === 0, blocking, advice, unslottedTexts };
}
/** Un número estable a partir de un texto: el mismo proyecto elige siempre igual. */
function hash(value) {
    let h = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
        h ^= value.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967296;
}
const IMAGE_MEDIA = new Set(['image', 'video', 'avatar']);
export function scoreTemplate(template, request) {
    const facets = template.facets;
    const category = facets.category ?? template.category ?? undefined;
    if (request.category && category !== request.category)
        return null;
    if (request.exclude?.includes(template.id))
        return null;
    // Ni el giro ni el estilo puntúan: un bloque sirve a cualquier negocio y
    // toma el aspecto de la receta del sitio. Se elige por lo que es y cómo está
    // armado; el giro y el estilo pesan al elegir el template de sitio.
    let score = 0;
    if (request.colorScheme && facets.colorScheme?.length) {
        score += facets.colorScheme.includes(request.colorScheme) ? 2 : -1;
    }
    if (request.hasImages === false && facets.media?.some((media) => IMAGE_MEDIA.has(media)))
        score -= 4;
    if (request.avoidLayouts?.length && facets.layout?.some((layout) => request.avoidLayouts.includes(layout)))
        score -= 2;
    if (request.terms?.length) {
        const haystack = normalizeSearchText([template.name, template.description ?? '', ...Object.values(facets).flat()].join(' '));
        score += request.terms.reduce((total, term) => total + (haystack.includes(term) ? 1 : 0), 0);
    }
    // Lo usado sube un poco; lo recién publicado también, para que lo nuevo
    // llegue a verse y no gane siempre el más viejo.
    score += Math.log10((template.usesCount ?? 0) + 1) * 0.5;
    if (template.publishedAt) {
        const age = ((request.now ?? new Date()).getTime() - new Date(template.publishedAt).getTime()) / 86_400_000;
        if (age >= 0 && age < 30)
            score += 0.75;
    }
    score += hash(template.id + (request.seed ?? '')) * 0.5;
    return score;
}
/** Los candidatos de una petición, del mejor al peor. */
export function rankTemplates(templates, request) {
    return templates
        .map((template) => ({ template, score: scoreTemplate(template, request) }))
        .filter((entry) => entry.score !== null)
        .sort((left, right) => right.score - left.score)
        .map((entry) => entry.template);
}
/** Las palabras de un mensaje que sirven para desempatar. */
export function searchTerms(message) {
    return [...new Set(normalizeSearchText(message).split(/[^a-z0-9]+/).filter((term) => term.length >= 4))];
}
//# sourceMappingURL=templateSelection.js.map